import type { App } from "@pentacode/core";
import { fullVersion } from "@pentacode/core";
import type * as SentryBrowser from "@sentry/browser";
import Bowser from "bowser";
import { IS_DEV } from "../env";

type SentrySdk = typeof SentryBrowser;

let initialized = false;

// The SDK is behind a dynamic import so it ships as its own lazy chunk
// (vendor-sentry) instead of in the initial bundle; `load` is injectable for
// tests, mirroring `ensureAssistantLoaded`.
export async function initSentry(
    app: Pick<App, "company" | "subscribe">,
    load: () => Promise<SentrySdk> = () => import("@sentry/browser")
) {
    if (initialized) {
        return;
    }
    initialized = true;

    try {
        configureSentry(await load(), app);
    } catch (e) {
        // The chunk can fail to arrive (offline, CSP, ad blocker). Swallow it —
        // Sentry itself isn't up to report it — and drop the latch so a later
        // call can retry.
        initialized = false;
        console.error("Failed to initialize Sentry", e);
    }
}

function configureSentry(Sentry: SentrySdk, app: Pick<App, "company" | "subscribe">) {
    // Session Replay (rrweb) is disabled entirely: on DOM-heavy pages like /roster
    // it records every mutation and accumulates hundreds of MB of heap over a
    // normal session. Error + performance tracing remain enabled.
    Sentry.init({
        release: fullVersion,
        dsn: "https://dbc681fc0cc678ba1f86f99585d2eec1@o4507944962621440.ingest.de.sentry.io/4507945168994384",
        integrations: [Sentry.browserTracingIntegration()],
        // Tracing — sample 10% of transactions. 100% was wrapping ~50 callbacks per
        // interaction and hit Sentry's ingest rate limit (429s).
        tracesSampleRate: 0.1,
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ["localhost", /^https:\/\/.*\.pentacode\.app/],
        allowUrls: [/^https:\/\/(manage|admin|api|jobs|time|time2|perso)\.(pentacode\.app)/],
        environment: IS_DEV ? "development" : "production",
        ignoreErrors: [
            "Die Applikation konnte keine Verbindung zum Pentacode Server herstellen.",
            "Sie haben keine ausreichenden Berechtigungen für diese Aktion!",
            "Failed to execute 'define' on 'CustomElementRegistry': the name \"ptc-update-notification\" has already been used with this registry",
            "Failed to execute 'transaction' on 'IDBDatabase': The database connection is closing.",
            "IDBDatabase.transaction: Can't start a transaction on a closed database",
        ],
        beforeSend(event) {
            // here you can modify or filter events before they are sent to Sentry, see:  https://docs.sentry.io/platforms/javascript/configuration/filtering/#using-before-send
            return event;
        },
        beforeSendTransaction(event) {
            // here you can modify or filter transaction events before they are sent to Sentry, see: https://docs.sentry.io/platforms/javascript/configuration/filtering/#using-beforesendtransaction
            return event;
        },
        beforeSendSpan(span) {
            // here you can modify or filter spans before they are sent to Sentry, see: https://docs.sentry.io/platforms/javascript/configuration/filtering/#filtering-spans
            return span;
        },
    });

    try {
        const browser = Bowser.getParser(window.navigator.userAgent);
        const browserName = browser.getBrowserName();
        const browserMajorVersion = browser.getBrowser().version?.split(".")[0];

        if (browserName && browserMajorVersion) {
            Sentry.setTag("browser.major_version", `${browserName} ${browserMajorVersion}`);
        }
    } catch (e) {
        console.error("Failed to detect browser version", e);
    }

    const setCompanyTag = () => Sentry.setTag("ptc_company_id", app.company?.id);
    app.subscribe(setCompanyTag);
    setCompanyTag();
}
